import { createMachine, assign } from 'xstate';
import { createEmptyGrid } from './utils/utils';
import accepted from './dictionary/fr-5-accepted.json';
import type { HistoryStore } from './history/store';
import type { HistoryEntry } from './history/types';
import type { GameSaveStore } from './history/game-save.store';


/* ──────────────────────────────── */
/* Types                            */
/* ──────────────────────────────── */

export type SerializableWordleContext = Omit<
  WordleContext,
  'rows' | 'cols' | 'revealedCorrectLetters'
> & {
  revealedCorrectLetters: Record<number, string>;
};

export type WordleEvent =
  | {
      type: 'START_GAME';
      dateKey: string;
      targetWord: string;
      history?: HistoryEntry;
      savedState?: SerializableWordleContext;
      hardcore?: boolean;
    }
  | { type: 'LETTER_TYPED'; letter: string }
  | { type: 'BACKSPACE' }
  | { type: 'SUBMIT_GUESS' }
  | { type: 'TOGGLE_AUTO_REVEAL' }
  | { type: 'TIME_UP' };

export type TileState = 'default' | 'correct' | 'present' | 'absent';

export type Tile = {
  letter: string | null;
  state: TileState;
  locked?: boolean;
};

export type WordleContext = {
  rows: number;
  cols: number;

  grid: Tile[][];
  currentRow: number;
  cursorCol: number;
  shakeRow: number | null;

  targetWord: string;
  dateKey: string | null;

  autoRevealCorrectLetters: boolean;
  revealedCorrectLetters: Map<number, string>;

  locked: boolean;

  /** Mode hardcore : quand true, un event TIME_UP force la défaite. */
  hardcore: boolean;

  /**
   * Échéance absolue du timer hardcore (ms epoch), armée à la validation de la
   * 1re proposition. Persistée avec la sauvegarde → survit reload/navigation
   * (anti-triche : impossible de remettre le compteur à zéro). `null` tant que
   * le timer n'est pas armé.
   */
  hardcoreDeadline: number | null;

  /**
   * Temps restant (ms) figé au moment de la victoire hardcore. `null` tant que
   * la partie n'est pas gagnée en hardcore. Alimente le chrono figé et les
   * stats de temps.
   */
  hardcoreTimeRemainingMs: number | null;
};

/* ──────────────────────────────── */
/* Helpers (TS-safe)                */
/* ──────────────────────────────── */

function findNextEditableCol(row: Tile[], start: number) {
  for (let i = start; i < row.length; i++) {
    const cell = row[i];
    if (!cell) continue;

    if (!cell.locked && cell.letter === null) {
      return i;
    }
  }
  return row.length;
}

function findPrevEditableCol(row: Tile[], start: number) {
  for (let i = start; i >= 0; i--) {
    const cell = row[i];
    if (!cell) continue;

    if (!cell.locked) {
      return i;
    }
  }
  return -1;
}

function buildGridFromGuesses(
  guesses: string[],
  rows: number,
  cols: number,
  targetWord: string
): Tile[][] {
  const grid = createEmptyGrid(rows, cols);

  guesses.forEach((word, rowIndex) => {
    const row = grid[rowIndex];
    if (!row) return;

    const target = targetWord.split('');

    word.split('').forEach((letter, colIndex) => {
      const cell = row[colIndex];
      if (!cell) return;
      cell.letter = letter;
      cell.state = 'absent';
    });

    word.split('').forEach((letter, i) => {
      const cell = row[i];
      if (!cell) return;
      if (letter === target[i]) {
        cell.state = 'correct';
        target[i] = '_';
      }
    });

    word.split('').forEach((letter, i) => {
      const cell = row[i];
      if (!cell || cell.state === 'correct') return;
      const idx = target.indexOf(letter);
      if (idx !== -1) {
        cell.state = 'present';
        target[idx] = '_';
      }
    });
  });

  return grid;
}

/* ──────────────────────────────── */
/* Machine                          */
/* ──────────────────────────────── */

export const createWordleMachine = (params: {
  rows: number;
  cols: number;
  historyStore: HistoryStore;
  saveStore?: GameSaveStore;
  acceptedWords?: string[];
  /** Durée du timer hardcore en ms (défaut 60 000). */
  hardcoreTimeLimitMs?: number;
}) =>
  createMachine(
    {
      types: {} as {
        context: WordleContext;
        events: WordleEvent;
      },

      id: 'wordle',
      initial: 'idle',

      context: {
        rows: params.rows,
        cols: params.cols,

        grid: createEmptyGrid(params.rows, params.cols),
        currentRow: 0,
        cursorCol: 0,
        shakeRow: null,

        targetWord: '',
        dateKey: null,

        autoRevealCorrectLetters: true,
        revealedCorrectLetters: new Map(),

        locked: false,

        hardcore: false,
        hardcoreDeadline: null,
        hardcoreTimeRemainingMs: null,
      },

      on: {
        START_GAME: {
          target: '.typing',
          actions: ['startGame', 'saveGameStateIfNew'],
        },
        TOGGLE_AUTO_REVEAL: {
          actions: ['toggleAutoReveal', 'saveGameState'],
        },
        // Timer hardcore écoulé (émis par l'app) → défaite immédiate.
        TIME_UP: {
          guard: 'hardcoreInProgress',
          target: '.lost',
        },
      },

      states: {
        idle: {},

        typing: {
          on: {
            LETTER_TYPED: {
              guard: 'canType',
              actions: ['typeLetter', 'saveGameState'],
            },
            BACKSPACE: {
              guard: 'canType',
              actions: ['deleteLetter', 'saveGameState'],
            },
            SUBMIT_GUESS: [
              { guard: 'canSubmitGuess', target: 'checking' },
              { target: 'invalid' },
            ],
          },
        },

        invalid: {
          entry: 'triggerShake',
          after: {
            400: { actions: 'clearShake', target: 'typing' },
          },
        },

        checking: {
          entry: ['scoreCurrentRow', 'persistProgress', 'saveGameState'],
          always: [
            { guard: 'isWinningGuess', target: 'won' },
            {
              guard: 'hasRemainingAttempts',
              actions: [
                'advanceToNextRow',
                'prepareNextRow',
                'armHardcoreTimer',
                'saveGameState',
              ],
              target: 'typing',
            },
            { target: 'lost' },
          ],
        },

        won: {
          entry: [
            'snapshotHardcoreTime',
            'persistWin',
            'lockGame',
            'clearGameSave',
          ],
        },

        lost: {
          entry: ['persistLoss', 'lockGame', 'clearGameSave'],
        },
      },
    },
    {
      /* ─────────────────────────── */
      /* Actions                     */
      /* ─────────────────────────── */

      actions: {
        startGame: assign(({ context, event }) => {
          if (event.type !== 'START_GAME') return {};

          // Priorité à l'état sauvegardé s'il existe
          if (event.savedState) {
            const revealedCorrectLetters = new Map<number, string>();
            Object.entries(event.savedState.revealedCorrectLetters).forEach(
              ([key, value]) => {
                revealedCorrectLetters.set(Number(key), value);
              }
            );

            return {
              ...event.savedState,
              rows: context.rows,
              cols: context.cols,
              revealedCorrectLetters,
            };
          }

          if (event.history) {
            const nextRow = event.history.currentRow;
            const nextRowGuess = event.history.guesses[nextRow] ?? '';

            return {
              targetWord: event.history.targetWord,
              dateKey: event.dateKey,
              grid: buildGridFromGuesses(
                event.history.guesses,
                context.rows,
                context.cols,
                event.history.targetWord
              ),
              currentRow: nextRow,
              cursorCol: nextRowGuess.length,
              locked: event.history.status !== 'in-progress',
              hardcore: event.hardcore ?? context.hardcore,
              hardcoreDeadline: null,
              hardcoreTimeRemainingMs:
                event.history.hardcoreTimeRemainingMs ?? null,
            };
          }

          return {
            targetWord: event.targetWord,
            dateKey: event.dateKey,
            grid: createEmptyGrid(context.rows, context.cols),
            currentRow: 0,
            cursorCol: 0,
            shakeRow: null,
            revealedCorrectLetters: new Map(),
            locked: false,
            hardcore: event.hardcore ?? false,
            hardcoreDeadline: null,
            hardcoreTimeRemainingMs: null,
          };
        }),

        toggleAutoReveal: assign(({ context }) => ({
          autoRevealCorrectLetters: !context.autoRevealCorrectLetters,
        })),

        // À la victoire : fige le temps restant (hardcore uniquement) pour le
        // chrono figé et les stats. Exécuté AVANT persistWin (XState v5 applique
        // les assign dans l'ordre → persistWin lit la valeur à jour).
        snapshotHardcoreTime: assign(({ context }) => {
          if (!context.hardcore || context.hardcoreDeadline == null) {
            return { hardcoreTimeRemainingMs: null };
          }
          return {
            hardcoreTimeRemainingMs: Math.max(
              0,
              context.hardcoreDeadline - Date.now()
            ),
          };
        }),

        // Arme une deadline FRAÎCHE exactement à la 1re proposition validée
        // (currentRow atteint 1) — ce qui écrase toute deadline résiduelle
        // d'une sauvegarde périmée. Les propositions suivantes (currentRow > 1)
        // et les restaurations (START_GAME, hors de cette transition) ne
        // touchent pas la deadline → elle survit reload/navigation (anti-triche).
        armHardcoreTimer: assign(({ context }) => {
          if (!context.hardcore) return {};
          if (context.currentRow !== 1) return {};
          return {
            hardcoreDeadline: Date.now() + (params.hardcoreTimeLimitMs ?? 60_000),
          };
        }),

        typeLetter: assign(({ context, event }) => {
          if (event.type !== 'LETTER_TYPED') return {};

          const grid = structuredClone(context.grid);
          const row = grid[context.currentRow];
          if (!row) return {};

          const col = findNextEditableCol(row, context.cursorCol);
          if (col >= context.cols) return {};

          const cell = row[col];
          if (!cell) return {};

          cell.letter = event.letter;

          return {
            grid,
            cursorCol: findNextEditableCol(row, col + 1),
          };
        }),

        deleteLetter: assign(({ context }) => {
          const grid = structuredClone(context.grid);
          const row = grid[context.currentRow];
          if (!row) return {};

          const col = findPrevEditableCol(row, context.cursorCol - 1);
          if (col < 0) return {};

          const cell = row[col];
          if (!cell) return {};

          if (!cell.locked) {
            cell.letter = null;
          }

          return {
            grid,
            cursorCol: col,
          };
        }),

        advanceToNextRow: assign(({ context }) => ({
          currentRow: context.currentRow + 1,
          cursorCol: 0,
        })),

        triggerShake: assign(({ context }) => ({
          shakeRow: context.currentRow,
        })),

        clearShake: assign(() => ({
          shakeRow: null,
        })),

        scoreCurrentRow: assign(({ context }) => {
          const grid = structuredClone(context.grid);
          const row = grid[context.currentRow];
          if (!row) return {};

          const target = context.targetWord.split('');
          const revealed = new Map(context.revealedCorrectLetters);

          row.forEach(cell => {
            if (cell.letter) cell.state = 'absent';
          });

          row.forEach((cell, i) => {
            if (cell.letter === target[i]) {
              cell.state = 'correct';
              revealed.set(i, cell.letter!);
              target[i] = '_';
            }
          });

          row.forEach(cell => {
            if (cell.state === 'correct' || !cell.letter) return;
            const idx = target.indexOf(cell.letter);
            if (idx !== -1) {
              cell.state = 'present';
              target[idx] = '_';
            }
          });

          return { grid, revealedCorrectLetters: revealed };
        }),

        prepareNextRow: assign(({ context }) => {
          if (!context.autoRevealCorrectLetters) return {};

          const grid = structuredClone(context.grid);
          const row = grid[context.currentRow];
          if (!row) return {};

          context.revealedCorrectLetters.forEach((letter, index) => {
            const cell = row[index];
            if (!cell || cell.letter !== null) return;

            cell.letter = letter;
            cell.locked = true;
          });

          return {
            grid,
            cursorCol: findNextEditableCol(row, 0),
          };
        }),

        persistProgress: ({ context }) => {
          if (!context.dateKey) return;

          params.historyStore.save({
            dateKey: context.dateKey,
            targetWord: context.targetWord,
            status: 'in-progress',
            guesses: context.grid
              .slice(0, context.currentRow + 1)
              .map(r => r.map(t => t.letter ?? '').join('')),
            currentRow: context.currentRow,
          });
        },

        persistWin: ({ context }) => {
          params.historyStore.save({
            dateKey: context.dateKey!,
            targetWord: context.targetWord,
            status: 'won',
            guesses: context.grid
              .slice(0, context.currentRow + 1)
              .map(r => r.map(t => t.letter ?? '').join('')),
            currentRow: context.currentRow + 1,
            finishedAt: Date.now(),
            hardcoreTimeRemainingMs:
              context.hardcoreTimeRemainingMs ?? undefined,
          });
        },

        persistLoss: ({ context }) => {
          params.historyStore.save({
            dateKey: context.dateKey!,
            targetWord: context.targetWord,
            status: 'lost',
            guesses: context.grid.map(r =>
              r.map(t => t.letter ?? '').join('')
            ),
            currentRow: context.rows,
            finishedAt: Date.now(),
          });
        },

        lockGame: assign(() => ({ locked: true })),

        saveGameState: ({ context }) => {
          params.saveStore?.save(context);
        },

        saveGameStateIfNew: ({ context, event }) => {
          // Ne sauvegarde que si c'est une nouvelle partie (pas de history, pas de savedState)
          // On écrase toujours la sauvegarde précédente (une seule sauvegarde globale)
          if (event.type === 'START_GAME' && !event.history && !event.savedState) {
            params.saveStore?.save(context);
          }
        },

        clearGameSave: ({ context }) => {
          if (context.dateKey) {
            params.saveStore?.clear(context.dateKey);
          }
        },
      },

      /* ─────────────────────────── */
      /* Guards                      */
      /* ─────────────────────────── */

      guards: {
        canType: ({ context }) => !context.locked,

        canSubmitGuess: ({ context }) => {
          if (context.locked) return false;
        
          const row = context.grid[context.currentRow];
          if (!row) return false;
        
          const isRowComplete = row.every(cell => cell.letter !== null);
          if (!isRowComplete) return false;
        
          const word = row.map(t => t.letter).join('');
          const acceptedWords = params.acceptedWords ?? accepted;
          return acceptedWords.includes(word);
        },

        isWinningGuess: ({ context }) => {
          const row = context.grid[context.currentRow];
          if (!row) return false;
          return (
            row.map(t => t.letter ?? '').join('') === context.targetWord
          );
        },

        hasRemainingAttempts: ({ context }) =>
          context.currentRow < context.rows - 1,

        // Le timer ne peut faire perdre que si le mode hardcore est actif
        // et que la partie est encore en cours (non verrouillée).
        hardcoreInProgress: ({ context }) =>
          context.hardcore && !context.locked && context.dateKey != null,
      },
    }
  );
